import React, { forwardRef } from "react"
import { twMerge } from "tailwind-merge"
import { cx } from "lib/helpers"
import ButtonIcon, { ButtonIconProps } from "components/Forms/Button/ButtonIcon"
import * as styles from "components/Forms/Button/button.module.css"

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonIconProps {
  text?: string | React.ReactNode
  fluid?: boolean
  className?: string
  onClick?: () => void
  secondaryHover?: boolean
  mobileHoverDisabled?: boolean
}

const Button = forwardRef(
  (
    {
      text = `Submit`,
      disabled = false,
      fluid = false,
      loading = false,
      secondary = false,
      addCheckMark = false,
      className = ``,
      type = `button`,
      secondaryHover = false,
      mobileHoverDisabled = false,
      icon: Icon,
      ...otherProps
    }: ButtonProps,
    ref
  ) => {
    const baseStyles = cx(
      `relative flex appearance-none items-center justify-center rounded-none border border-black px-2 py-3 font-sans text-sm font-medium uppercase tracking-wider text-white outline-offset-8 hover:border-dark-gray md:px-8`,
      { "hover:underline": secondaryHover }
    )

    const primaryStyle = cx(`bg-black`, {
      "hover:border hover:bg-white hover:text-black":
        !secondaryHover && !disabled && !mobileHoverDisabled,
      "md:hover:border md:hover:bg-white md:hover:text-black":
        mobileHoverDisabled
    })

    const secondaryStyle = cx(
      `bg-white px-3 py-[calc(0.75rem+1px)] text-black`,
      {
        "border hover:border-black hover:bg-black hover:text-white":
          !secondaryHover && !disabled
      }
    )

    const loadingHoverStyles = secondary ? `hover:bg-white` : `hover:bg-black`

    const fluidStyles = `w-full`

    const disabledStyles = `cursor-not-allowed border-custom-disabled bg-custom-disabled text-white`

    return (
      <button
        type={type.toLowerCase() === `submit` ? `submit` : `button`}
        {...otherProps}
        disabled={disabled}
        aria-busy={loading}
        className={cx(
          styles.focusRing,
          { group: addCheckMark },
          twMerge(
            className,
            baseStyles,
            secondary ? secondaryStyle : primaryStyle,
            fluid && fluidStyles,
            disabled && disabledStyles,
            loading && !disabled && loadingHoverStyles
          )
        )}
        ref={ref as React.Ref<HTMLButtonElement>}
      >
        <ButtonIcon
          loading={loading}
          secondary={secondary}
          icon={Icon}
          disabled={disabled}
          addCheckMark={addCheckMark}
        />

        {!(loading && !addCheckMark) && !(!loading && addCheckMark) && text}
      </button>
    )
  }
)

export default Button
